/**
 * Calls listeners after every write request (anything but GET/HEAD/OPTIONS) this tab
 * sends through axios or apiClient, whether it succeeded or not: a request that failed
 * on the client may still have been applied on the server.
 *
 * Caches of server data use it to drop what a write may have changed.
 */

import axios from 'axios';
import { apiClient, API_BASE_URL, onRequestNotSent } from '@/store/api/config';

const READ_METHODS = new Set(['get', 'head', 'options']);
const listeners = new Set();
let installed = false;

const isWrite = (config) => !READ_METHODS.has(String(config?.method || 'get').toLowerCase());

// Writes sent and not answered yet (other than SOAP note saves and lookups): id -> when
// sent. The id is kept on the request's config, so the answer to a retry (a 401 refreshed
// and sent again, as a new config) still clears it. A request refused before it was sent
// (its login couldn't be refreshed) is cleared through onRequestNotSent; one whose answer
// never comes stops counting after WRITE_IN_FLIGHT_MAX_MS.
const writesInFlight = new Map();
export const WRITE_IN_FLIGHT_MAX_MS = 2 * 60 * 1000;
const WRITE_ID = '__panelWriteId';
let lastWriteId = 0;
// Told when the last write in flight is answered (see onPanelWritesSettled)
const settledListeners = new Set();

const forgetWrite = (config) => {
  const id = config?.[WRITE_ID];
  if (id === undefined || !writesInFlight.delete(id)) return;
  if (writesInFlight.size > 0) return;
  settledListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // a listener must never break the request
    }
  });
};

const apiBasePath = (() => {
  try {
    return new URL(API_BASE_URL).pathname.replace(/\/+$/, '');
  } catch {
    return '';
  }
})();

/** The request's path without origin, API base path or query, e.g. '/soap-note/saved/123'. */
export const requestPath = (config) => {
  let path = String(config?.url || '')
    .replace(/^[a-z]+:\/\/[^/]+/i, '')
    .replace(/[?#].*$/, '');
  if (apiBasePath && path.startsWith(`${apiBasePath}/`)) path = path.slice(apiBasePath.length);
  return path.startsWith('/') ? path : `/${path}`;
};

// Writes that change neither a patient's documents, form or summary nor the SOAP
// templates: SOAP note saves, AI helpers, the live-update stream ticket, and the SOAP
// template lookup (a POST that only reads, sent each time a note builds its plan or
// medications text). The patient panel and template caches ignore these.
const SOAP_NOTE_OR_LOOKUP_PATHS = ['/soap-note', '/stream-ticket', '/api/soap-note-templates/selected-content/batch'];
const SOAP_NOTE_OR_LOOKUP_PREFIXES = ['/soap-note/', '/ai/'];

export const isSoapNoteOrLookupWrite = (config) => {
  const path = requestPath(config);
  return SOAP_NOTE_OR_LOOKUP_PATHS.includes(path)
    || SOAP_NOTE_OR_LOOKUP_PREFIXES.some((prefix) => path.startsWith(prefix));
};

const notify = (config) => {
  if (!config) return;
  forgetWrite(config);
  if (!isWrite(config)) return;
  listeners.forEach((listener) => {
    try {
      listener(config);
    } catch {
      // a cache listener must never break the request
    }
  });
};

const install = (instance) => {
  instance?.interceptors?.request?.use?.((config) => {
    if (config && isWrite(config) && !isSoapNoteOrLookupWrite(config)) {
      if (config[WRITE_ID] === undefined) {
        lastWriteId += 1;
        config[WRITE_ID] = lastWriteId;
      }
      writesInFlight.set(config[WRITE_ID], Date.now());
    }
    return config;
  });
  instance?.interceptors?.response?.use?.(
    (response) => {
      notify(response?.config);
      return response;
    },
    (error) => {
      notify(error?.config);
      return Promise.reject(error);
    },
  );
};

/**
 * @param {(config: object) => void} listener - gets the request's axios config
 * @returns {() => void} unsubscribe
 */
const ensureInstalled = () => {
  if (installed) return;
  installed = true;
  install(axios);
  if (apiClient && apiClient !== axios) install(apiClient);
  if (typeof onRequestNotSent === 'function') onRequestNotSent(forgetWrite);
};

export const onApiMutation = (listener) => {
  ensureInstalled();
  listeners.add(listener);
  return () => listeners.delete(listener);
};

/**
 * Calls `listener` each time the last write in flight is answered (or refused before it
 * was sent), so something waiting for hasPanelWritesInFlight() to end can act then.
 * @returns {() => void} unsubscribe
 */
export const onPanelWritesSettled = (listener) => {
  ensureInstalled();
  settledListeners.add(listener);
  return () => settledListeners.delete(listener);
};

/**
 * Whether a write that may change the patient panels (an upload, an approval, a delete)
 * has been sent and not answered yet. Only counts once onApiMutation has a listener.
 */
export const hasPanelWritesInFlight = (now = Date.now()) => {
  for (const [id, startedAt] of writesInFlight) {
    if (now - startedAt <= WRITE_IN_FLIGHT_MAX_MS) return true;
    writesInFlight.delete(id);
  }
  return false;
};
